import { KeyboardEvent, ReactNode, useState } from 'react'
import { styled } from 'baseui'
import SwipeRow from '@/components/SwipeRow/SwipeRow'
import { ToolButton, ToolButtonLabel } from '@/components/ToolButton/ToolButton'
import { lightTheme } from '@/customTheme'

export interface SliderTab {
  key: string
  label: string
  icon: ReactNode
  /** The slider shown while this tab is picked. */
  content: ReactNode
}

/** A lead control (e.g. Fix Lighting, colors), the picked slider, then its tabs — 20px apart. */
export const SliderTabsLayout = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
})

/**
 * One slider at a time, picked from a sideways row of icon-over-name tabs
 * (the tool rail's buttons). Place inside SliderTabsLayout. Every slider stays
 * mounted, so values and callbacks are the same as a stacked layout.
 */
export default function SliderTabs({ tabs }: { tabs: SliderTab[] }) {
  const [selectedKey, setSelectedKey] = useState(tabs[0]?.key)
  const activeKey = tabs.some((tab) => tab.key === selectedKey) ? selectedKey : tabs[0]?.key

  return (
    <>
      {tabs.map((tab) => (
        <div key={tab.key} role="tabpanel" aria-label={tab.label} hidden={activeKey !== tab.key}>
          {tab.content}
        </div>
      ))}
      <SwipeRow role="tablist" $style={{ gap: 0 }}>
        {tabs.map((tab, index) => (
          <ToolButton
            type="button"
            role="tab"
            key={tab.key}
            aria-label={tab.label}
            aria-selected={activeKey === tab.key}
            tabIndex={activeKey === tab.key ? 0 : -1}
            $selected={activeKey === tab.key}
            $style={{ flexShrink: 0, ':focus-visible': { outline: `2px solid ${lightTheme.colors.primary}`, outlineOffset: '-2px' } }}
            onClick={() => setSelectedKey(tab.key)}
            onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
              let nextIndex: number
              if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
              else if (event.key === 'ArrowLeft') nextIndex = (index + tabs.length - 1) % tabs.length
              else if (event.key === 'Home') nextIndex = 0
              else if (event.key === 'End') nextIndex = tabs.length - 1
              else return
              event.preventDefault()
              event.stopPropagation()
              setSelectedKey(tabs[nextIndex].key)
              const next = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]
              next?.focus()
            }}
          >
            {tab.icon}
            <ToolButtonLabel text={tab.label} />
          </ToolButton>
        ))}
      </SwipeRow>
    </>
  )
}
